import type { AdminOrder, AdminOrderStatus } from '../mocks/adminOrders';

const ORDERS_KEY = 'sipdash_orders';
const MOCK_HIDDEN_KEY = 'sipdash_mock_orders_hidden';

export function getAllOrders(): AdminOrder[] {
  try {
    const raw = localStorage.getItem(ORDERS_KEY);
    return raw ? (JSON.parse(raw) as AdminOrder[]) : [];
  } catch {
    return [];
  }
}

function saveAll(orders: AdminOrder[]): void {
  localStorage.setItem(ORDERS_KEY, JSON.stringify(orders));
  // Dispatch custom event so admin page can react immediately
  try {
    window.dispatchEvent(new Event('sipdash_order_saved'));
  } catch {
    // ignore
  }
}

export function saveOrder(order: AdminOrder): void {
  const orders = getAllOrders();
  const idx = orders.findIndex((o) => o.id === order.id);
  if (idx >= 0) {
    orders[idx] = order;
  } else {
    orders.unshift(order);
  }
  saveAll(orders);
}

export function updateOrderStatus(id: string, status: AdminOrderStatus): void {
  const orders = getAllOrders();
  saveAll(orders.map((o) => (o.id === id ? { ...o, status } : o)));
}

export function confirmOrderWithRider(id: string, rider: string): void {
  const orders = getAllOrders();
  saveAll(
    orders.map((o) =>
      o.id === id ? { ...o, status: 'pending' as AdminOrderStatus, rider } : o
    )
  );
}

export function getPendingPayments(): AdminOrder[] {
  return getAllOrders().filter((o) => o.status === 'payment_pending');
}

export function getConfirmedOrders(): AdminOrder[] {
  return getAllOrders().filter((o) => o.status !== 'payment_pending');
}

export function getOrdersByUser(userId: string): AdminOrder[] {
  return getAllOrders().filter((o) => o.userId === userId);
}

export function generateOrderId(): string {
  return `SDG-${Math.floor(10000 + Math.random() * 90000)}`;
}

export function clearAllOrders(): void {
  localStorage.removeItem(ORDERS_KEY);
  localStorage.setItem(MOCK_HIDDEN_KEY, '1');
}

export function areMockOrdersHidden(): boolean {
  return localStorage.getItem(MOCK_HIDDEN_KEY) === '1';
}

export function resetMockOrdersVisibility(): void {
  localStorage.removeItem(MOCK_HIDDEN_KEY);
}

export function cancelOrder(id: string): boolean {
  const orders = getAllOrders();
  const order = orders.find((o) => o.id === id);
  if (!order || order.status !== 'payment_pending') return false;
  saveAll(orders.map((o) => (o.id === id ? { ...o, status: 'cancelled' as AdminOrderStatus } : o)));
  return true;
}

export function getTodayCancelledOrders(): AdminOrder[] {
  const startOfDay = new Date();
  startOfDay.setHours(0, 0, 0, 0);
  return getAllOrders().filter(
    (o) => o.status === 'cancelled' && new Date(o.createdAt) >= startOfDay
  );
}
